import { faHome, faSave } from "@fortawesome/pro-duotone-svg-icons";
import { Toolbar, ToolbarSeparator } from "@progress/kendo-react-buttons";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { Form, FormElement } from "@progress/kendo-react-form";
import { Loader } from "@progress/kendo-react-indicators";
import { Input } from "@progress/kendo-react-inputs";
import { Breadcrumb, TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { allRoles, userData } from "../Common/Store/CommonSignals.jsx";
import { ColorChangingIcon } from "../Common/UI/ColorChangingIcon.jsx";
import { Resources } from "./Resources.js";
import ResourcesTab from "./ResourcesTab";
import { createRole, updateRole } from "./RolesSlice";

function NewRole() {
  const urlParams = new URLSearchParams(window.location.search);
  const id = urlParams.get("roleId");
  const [isSaving, setIsSaving] = useState();
  const [selected, setSelected] = useState(0);
  const [role, setRole] = useState({});
  const [voSelectedObjects, setVoSelectedObjects] = useState([]);
  const [voState, setVoState] = useState({
    resources: Resources.ViewOnly,
    selectedResources: [],
  });
  const [modSelectedObjects, setModSelectedObjects] = useState([]);
  const [modState, setModState] = useState({
    resources: Resources.Modify,
    selectedResources: [],
  });

  const fetchedRole = allRoles.value?.find((r) => r.id === id);
  const groups = [
    { displayName: "ATLAS_Admin", id: "8007faf5-493c-4179-aa8e-017a49391c6c" },
    { displayName: "ATLAS_PowerUser", id: "0497a4d4-a91f-4616-8bd6-b21eb7b64caf" },
  ];
  const userEmail = userData.value?.userEmail;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSelect = (e) => {
    setSelected(e.selected);
  };

  const setEditRoleData = () => {
    const role = JSON.tryParse(JSON.stringify(fetchedRole));
    console.log("Editing Role: ", role);
    setRole(role);

    const modResources = modState.resources.map((r) => {
      const selected = role.permissions
        .find((p) => p.action === "Modify")
        .resourcesName.includes(r.resourceName);
      return { ...r, selected };
    });

    const voResources = voState.resources.map((r) => {
      const selected = role.permissions
        .find((p) => p.action === "ViewOnly")
        .resourcesName.includes(r.resourceName);
      return { ...r, selected };
    });

    setModState({
      resources: modResources.filter((r) => !r.selected),
      selectedResources: modResources.filter((r) => r.selected),
    });

    setVoState({
      resources: voResources.filter((r) => !r.selected),
      selectedResources: voResources.filter((r) => r.selected),
    });

    setModSelectedObjects(role.permissions.find((p) => p.action === "Modify").objects);
    setVoSelectedObjects(role.permissions.find((p) => p.action === "ViewOnly").objects);
  };

  useEffect(() => {
    if (id) {
      setEditRoleData();
    }
  }, [id]);

  const handleGroupChange = (e) => {
    const newRole = { ...role, groupId: e.value.map((v) => v.id) };
    setRole(newRole);
  };

  const handleSubmit = () => {
    const newRole = {
      id: id || uuidv4(),
      roleName: role.roleName,
      groupId: role.groupId,
      permissions: [
        {
          action: "Modify",
          resourceType: "Component",
          resourcesName: modState.selectedResources.map((r) => r.resourceName),
          objects: modSelectedObjects,
        },
        {
          action: "ViewOnly",
          resourceType: "Component",
          resourcesName: voState.selectedResources.map((r) => r.resourceName),
          objects: voSelectedObjects,
        },
      ],
    };
    setIsSaving(true);
    console.log("Submitting Role", newRole);
    if (id) {
      dispatch(
        updateRole({
          roleData: newRole,
          userName: userEmail,
        })
      )
        .then(() => {
          navigate("/roles");
          setIsSaving(false);
        })
        .catch((error) => {
          console.log("Error Updating Role", error);
          setIsSaving(false);
        });
    } else {
      dispatch(
        createRole({
          roleData: newRole,
          userName: userEmail,
        })
      )
        .then(() => {
          navigate("/roles");
          setIsSaving(false);
        })
        .catch((error) => {
          console.log("Error Submitting Role", error);
          setIsSaving(false);
        });
    }
  };

  return (
    <div className="!k-d-flex !k-flex-col !k-justify-content-start !k-rounded-lg !k-w-full !k-h-full">
      <Breadcrumb
        style={{ backgroundColor: "transparent" }}
        data={[
          {
            id: "home",
            icon: (
              <ColorChangingIcon
                icon={faHome}
                initialPrimaryColor="#0078D4"
                initialSecondaryColor="#00aeef"
                initialSize={16}
                hoverSize={16}
              />
            ),
          },
          { id: "viewRoles_Highlight", text: "Roles" },
          {
            id: "newRole_Edge",
            text: id && id === role?.id ? "Edit " + role.roleName : `Create New Role`,
          },
        ]}
        onItemSelect={(e) => {
          console.log(e);
          if (e.id === "home") {
            navigate("/");
          } else if (e.id.includes("viewRoles")) {
            navigate("/roles");
          }
        }}
      />
      <div className="k-d-flex k-flex-col k-h-full k-overflow-auto">
        <Toolbar
          size={null}
          className="k-w-full"
        >
          {!isSaving && (
            <ColorChangingIcon
              title="Save Dataset"
              name="btnDatasetSubmit"
              icon={faSave}
              initialPrimaryColor="#000"
              initialSecondaryColor="#5fd255"
              primaryOpacity={0.75}
              secondaryOpacity={1}
              initialSize={32}
              hoverSize={32}
              margin="5px 10px"
              onClick={() => handleSubmit()}
            />
          )}
          {isSaving && (
            <Loader
              type="converging-spinner"
              themeColor="primary"
              margin="7px 10px"
            />
          )}
          <ToolbarSeparator />
        </Toolbar>
        <section className="!k-rounded-bl-xl !k-rounded-br-xl k-bg-white k-p-2 !k-h-full k-overflow-auto k-border-toolbar k-border-solid k-border-1">
          <Form
            initialValues={role}
            render={() => (
              <FormElement style={{ minWidth: 800 }}>
                <h1>
                  <strong>{id ? "Edit " + role.roleName : "Create New Role"}</strong>
                </h1>
                <Input
                  id={"roleName"}
                  name={"roleName"}
                  label={"Role Name"}
                  component={Input}
                  required={true}
                  value={role.roleName}
                  onChange={(e) => setRole({ ...role, roleName: e.target.value })}
                />
                <MultiSelect
                  name={"groupId"}
                  required={true}
                  component={MultiSelect}
                  data={groups}
                  value={groups?.filter((g) => role?.groupId?.includes(g.id))}
                  textField={"displayName"}
                  dataItemKey={"id"}
                  label={"Group Name"}
                  onChange={handleGroupChange}
                />

                <div className="k-border-black k-border-solid k-border k-mt-5 !k-w-full">
                  <TabStrip
                    className="!k-w-full"
                    selected={selected}
                    onSelect={handleSelect}
                  >
                    <TabStripTab title="ViewOnly">
                      <ResourcesTab
                        className="!k-w-full"
                        state={voState}
                        setState={setVoState}
                        selectedObjects={voSelectedObjects}
                        setSelectedObjects={setVoSelectedObjects}
                      />
                    </TabStripTab>
                    <TabStripTab title="Modify">
                      <ResourcesTab
                        state={modState}
                        setState={setModState}
                        selectedObjects={modSelectedObjects}
                        setSelectedObjects={setModSelectedObjects}
                      />
                    </TabStripTab>
                  </TabStrip>
                </div>
              </FormElement>
            )}
          />
        </section>
      </div>
    </div>
  );
}

export default NewRole;
